import {
  BadgeDollarSign,
  Boxes,
  ExternalLink,
  Package,
  PiggyBank,
  ShoppingCart,
  Target,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatTile } from '@/components/marketing/MetricTile';
import { RankedList, StatRow } from '@/components/marketing/RankedList';
import { TrendChart } from '@/components/marketing/TrendChart';
import {
  ChannelStatusNotice,
  resolveChannelStatus,
} from '@/components/marketing/ChannelStatusNotice';
import { comparisonLabel } from '@/components/marketing/RangeSelector';
import { CHART_SERIES } from '@/lib/brand';
import { formatCurrency } from '@/lib/formatters';
import { useAmazonAnalytics } from '@/features/marketing/hooks';
import {
  formatCompactNumber,
  formatRatioPercent,
  MARKETING_RANGE_DAYS,
  pctChange,
  pctChangeInverted,
  type MarketingRange,
} from '@/features/marketing/range';

interface AmazonTabProps {
  range: MarketingRange;
}

/**
 * Amazon channel performance: seller revenue from SP-API alongside Amazon
 * Advertising spend and attributed sales.
 *
 * The headline metric for an Amazon seller is TACoS (total advertising cost of
 * sale) — ad spend as a share of *all* Amazon revenue, not just ad-attributed
 * revenue. It's the number that says whether advertising is buying
 * incremental growth or just cannibalising organic sales, which is why it gets
 * a tile of its own and needs both integrations to be meaningful.
 */
export function AmazonTab({ range }: AmazonTabProps) {
  const query = useAmazonAnalytics(range);
  const data = query.data;
  const totals = data?.totals;
  const previous = data?.previous;
  const ads = data?.ads;
  const deltaLabel = comparisonLabel(range);
  const loading = query.isLoading;
  const days = MARKETING_RANGE_DAYS[range];

  const status = resolveChannelStatus({
    isLoading: query.isLoading,
    error: query.error,
    connected: data?.connected,
    awaitingFirstSync: data?.awaitingFirstSync,
  });

  const orderStatusRows = (data?.statusMix ?? []).map((entry) => ({
    key: entry.status,
    label: formatStatus(entry.status),
    value: entry.orders,
    meta: formatCurrency(entry.revenueCents, { whole: true }),
  }));

  return (
    <div className="space-y-6">
      <ChannelStatusNotice
        status={status}
        channel="Amazon Seller Central"
        error={query.error instanceof Error ? query.error.message : null}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-headline text-foreground">
            Amazon channel
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {(data?.channels ?? []).map((channel) => (
            <Badge key={channel.id} variant="secondary">
              {channel.name}
            </Badge>
          ))}
          <Button variant="outline" size="sm" asChild>
            <a href="https://sellercentral.amazon.com" target="_blank" rel="noreferrer">
              Seller Central <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Amazon revenue"
          icon={BadgeDollarSign}
          loading={loading}
          value={formatCurrency(totals?.revenueCents ?? 0, { whole: true })}
          delta={pctChange(totals?.revenueCents ?? 0, previous?.revenueCents ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="Orders"
          icon={ShoppingCart}
          loading={loading}
          value={(totals?.orders ?? 0).toLocaleString()}
          delta={pctChange(totals?.orders ?? 0, previous?.orders ?? 0)}
          deltaLabel={deltaLabel}
          hint={
            totals && days
              ? `${(totals.orders / days).toFixed(1)} per day average`
              : undefined
          }
        />
        <StatTile
          label="Average order value"
          icon={Package}
          loading={loading}
          value={totals?.aovCents ? formatCurrency(totals.aovCents) : '—'}
          delta={pctChange(totals?.aovCents ?? 0, previous?.aovCents ?? 0)}
          deltaLabel={deltaLabel}
        />
        <StatTile
          label="TACoS"
          icon={Boxes}
          loading={loading}
          value={ads?.connected ? formatRatioPercent(totals?.tacos ?? 0, 2) : '—'}
          delta={
            ads?.connected
              ? pctChangeInverted(totals?.tacos ?? 0, previous?.tacos ?? 0)
              : null
          }
          deltaLabel={deltaLabel}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue and ad spend</CardTitle>
        </CardHeader>
        <CardContent>
          <TrendChart
            data={(data?.daily ?? []).map((point) => ({
              day: point.day,
              revenue: point.revenueCents / 100,
              spend: point.spendCents / 100,
              orders: point.orders,
            }))}
            height={300}
            formatLeftAxis={(value) => `$${formatCompactNumber(value)}`}
            formatRightAxis={formatCompactNumber}
            series={[
              {
                key: 'revenue',
                label: 'Revenue',
                color: CHART_SERIES[0],
                kind: 'area',
                format: (value) => formatCurrency(Math.round(value * 100)),
              },
              {
                key: 'spend',
                label: 'Ad spend',
                color: CHART_SERIES[4],
                kind: 'bar',
                format: (value) => formatCurrency(Math.round(value * 100)),
              },
              {
                key: 'orders',
                label: 'Orders',
                color: CHART_SERIES[1],
                kind: 'line',
                axis: 'right',
              },
            ]}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Advertising</CardTitle>
          </CardHeader>
          <CardContent>
            {ads?.connected ? (
              <div>
                <StatRow
                  label="Ad spend"
                  value={formatCurrency(ads.totals.spendCents, { whole: true })}
                />
                <StatRow
                  label="Attributed sales"
                  value={formatCurrency(ads.totals.revenueCents, { whole: true })}
                />
                <StatRow
                  label="ROAS"
                  value={ads.totals.roas ? `${ads.totals.roas.toFixed(2)}×` : '—'}
                />
                <StatRow
                  label="ACoS"
                  value={
                    ads.totals.revenueCents
                      ? formatRatioPercent(
                          ads.totals.spendCents / ads.totals.revenueCents,
                          1,
                        )
                      : '—'
                  }
                />
                <StatRow
                  label="Conversions"
                  value={formatCompactNumber(ads.totals.conversions)}
                />
                <StatRow
                  label="Cost per click"
                  value={ads.totals.cpcCents ? formatCurrency(ads.totals.cpcCents) : '—'}
                />
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Amazon Ads isn&apos;t connected.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={(ads?.campaigns ?? []).slice(0, 8).map((campaign) => ({
                key: campaign.key,
                label: campaign.name,
                value: campaign.spendCents,
                hint: `${formatCompactNumber(campaign.clicks)} clicks · ${campaign.conversions.toLocaleString()} conversions`,
                meta: campaign.roas
                  ? `${campaign.roas.toFixed(2)}× ROAS`
                  : 'No attributed sales',
              }))}
              formatValue={(value) => formatCurrency(value, { whole: true })}
              emptyLabel="No campaign data for this period."
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              Order status mix
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RankedList
              rows={orderStatusRows}
              formatValue={(value) => value.toLocaleString()}
              emptyLabel="No orders in this period."
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="h-4 w-4 text-muted-foreground" />
              Efficiency summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow
              label="Total Amazon revenue"
              value={formatCurrency(totals?.revenueCents ?? 0, { whole: true })}
            />
            <StatRow
              label="Ad spend"
              value={
                ads?.connected
                  ? formatCurrency(ads.totals.spendCents, { whole: true })
                  : '—'
              }
            />
            <StatRow
              label="Ad-attributed revenue"
              value={
                ads?.connected
                  ? formatCurrency(ads.totals.revenueCents, { whole: true })
                  : '—'
              }
            />
            <StatRow
              label="Organic revenue"
              value={
                ads?.connected && totals
                  ? formatCurrency(
                      Math.max(0, totals.revenueCents - ads.totals.revenueCents),
                      { whole: true },
                    )
                  : '—'
              }
            />
            <StatRow
              label="TACoS"
              value={ads?.connected ? formatRatioPercent(totals?.tacos ?? 0, 2) : '—'}
            />
          </CardContent>
        </Card>
      </div>

    </div>
  );
}

/** `so_status` values are snake_case; render them as sentence case. */
function formatStatus(status: string): string {
  const spaced = status.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
